import { all, call, fork, put, takeEvery, take } from "redux-saga/effects";
import { END, eventChannel } from "redux-saga";

import {
  createCourseError,
  createCourseSuccess,
  getCourseError,
  getCourseSuccess,
  getListCoursesError,
  getListCoursesSuccess,
  removeCourseError,
  removeCourseSuccess,
  updateCourseError,
  updateCourseSuccess,
  changeCourseStatusSuccess,
  changeCourseStatusError,
  setCourseRecommendationsSuccess,
  setCourseRecommendationsError,
  getCoursesForRecommendationsError,
  getCoursesForRecommendationsSuccess,
  getVideoLibraryForCourseOptionSuccess,
  getVideoLibraryForCourseOptionError,
  getPaginatedCourseListSuccess,
  getPaginatedCourseListError,
  getTutorsForCourseOptionSuccess,
  getTutorsForCourseOptionError,
} from "./actions";
import {
  CREATE_COURSE,
  GET_COURSE,
  GET_LIST_COURSE,
  REMOVE_COURSE,
  UPDATE_COURSE,
  CHANGE_COURSE_STATUS,
  SET_COURSE_RECOMMENDATIONS,
  GET_COURSES_FOR_RECOMMENDATIONS,
  GET_VIDEO_LIBRARY_FOR_COURSE_OPTION,
  GET_PAGINATED_COURSE_LIST,
  GET_TUTORS_FOR_COURSE_OPTION,
} from "./actionTypes";
import CourseService from "../../../services/CourseService";
import FilesService from "../../../services/FilesService";
import TutorService from "../../../services/TutorService";
import {
  uploadFailure,
  uploadInitiate,
  uploadProgress,
  uploadSuccess,
} from "../../progress/actions";
import errorCodes from "../../../constants/errorCodes";
import throwCustomError from "../../../helpers/throwCustomError";
import { MAX_COURSES_LIMIT } from "../../../constants/courses";
import VideoLibraryService from '../../../services/VideoLibraryService';

const getCourseListAsync = async (data) => {
  const { categoryId, model } = data;
  return await CourseService.getList(categoryId, model);
};

const getPaginatedCourseListAsync = async (data) => {
  // const { model } = data;
  return await CourseService.getPaginatedCourseList(data);
};

const getCoursesForRecommendationsListAsync = async (coursesCount = 0, coursesList = []) => {

  const { pagination: { totalCount }, data } = await CourseService.getAllCoursesList({
    limit: MAX_COURSES_LIMIT,
    offset: coursesCount,
  });
  coursesList = [...coursesList, ...data.map(({ id, title }) => ({ id, title }))]

  if (totalCount > coursesList.length) {
    coursesList = await getCoursesForRecommendationsListAsync(coursesList.length, coursesList)
  }

  return coursesList;
};

const getCourseAsync = async (id) => {
  return await CourseService.getOne(id);
};

const courseComposite = (model) => {
  const {
    aboutTutor,
    convertkitTagId,
    tutorId,
    equipment,
    included,
    lessons,
    overview,
    metaDescription,
    prices,
    title,
    urlPart,
    numberOfReviews,
    overallRating,
    courseLengthInHours,
    files,
    videoThumbnail,
    courseThumbnailImage,
    student0Image,
    student1Image,
    student2Image,
    studentsSays,
    isApplicableToSubscription,
    isCollaborationCourse,
    type,
    tags,
    fileId,
    isFreeCourse,
    provideFreeCourseTo,
  } = model;

  return {
    files,
    videoThumbnail,
    courseThumbnailImage,
    student0Image,
    student1Image,
    student2Image,
    tags,
    courseData: {
      aboutTutor,
      convertkitTagId,
      tutorId,
      equipment,
      included,
      lessons,
      overview,
      metaDescription,
      prices,
      title,
      urlPart,
      numberOfReviews,
      overallRating,
      courseLengthInHours,
      studentsSays,
      isApplicableToSubscription,
      isCollaborationCourse,
      type,
      fileId,
      isFreeCourse,
      provideFreeCourseTo,
    },
  };
};

export const getFilesArray = (files, thumbnailFiles, courseThumbnailImage, studentImages) => {
  const allFiles = [];
  
  if (!!files && !!files.length) {
    allFiles.push(...files);
  }
  if (!!thumbnailFiles && !!thumbnailFiles.length) {
    allFiles.push(...thumbnailFiles);
  }
  if (!!courseThumbnailImage && !!courseThumbnailImage.length) {
    allFiles.push(...courseThumbnailImage);
  }
  if (!!studentImages && !!studentImages.length) {
    allFiles.push(...studentImages);
  }
  
  return allFiles.length > 0 ? allFiles : null;
};

export const getFilesTotalSize = (files, thumbnailFiles, courseThumbnailImage, studentImages) => {
  let totalSize = 0;
  
  if (!!files && !!files.length) {
    totalSize += files[0].size;
  }
  if (!!thumbnailFiles && !!thumbnailFiles.length) {
    totalSize += thumbnailFiles[0].size;
  }
  if (!!courseThumbnailImage && !!courseThumbnailImage.length) {
    totalSize += courseThumbnailImage[0].size;
  }
  if (!!studentImages && !!studentImages.length) {
    totalSize += studentImages.reduce((sum, image) => sum + image.size, 0);
  }
  
  return totalSize;
};

const combineStudentImages = (student0Image, student1Image, student2Image) => {
  const studentImages = [];
  
  if (!!student0Image && !!student0Image.length) {
    studentImages.push(...student0Image);
  }
  if (!!student1Image && !!student1Image.length) {
    studentImages.push(...student1Image);
  }
  if (!!student2Image && !!student2Image.length) {
    studentImages.push(...student2Image);
  }
  
  return studentImages;
};

/**
 * Course files channel for files uploading watching
 * @param data<Object>
 * @param mode<"create"|"update"> - mode of files progress channel (create by default)
 */
const courseFilesChannel = (data, mode = "create") => {
  return eventChannel((emit) => {
    const { model } = data;
    const { files, videoThumbnail, courseData, courseThumbnailImage, student0Image, student1Image, student2Image, tags } = courseComposite(model);
    const id = mode === "create" ? data.categoryId : data.courseId;
    const actionFunc =
      mode === "create"
        ? CourseService.create(id, courseData)
        : CourseService.update(id, courseData);
    if (
      (!files || !files.length) &&
      (!videoThumbnail || !videoThumbnail.length) &&
      (!courseThumbnailImage || !courseThumbnailImage.length) &&
      (!student0Image || !student0Image.length) &&
      (!student1Image || !student1Image.length) &&
      (!student2Image || !student2Image.length)
    ) {
      // here is only one file and it's mandatory
      actionFunc
        .then(async (result) => {
          const course = result.data;

          if (!!tags && !!tags.length && (!files || !files.length)) {
            await CourseService.updateVideoTags(
              course.id,
              tags
            ).catch((error) => {
              console.log("error", error);
            });
          }

          emit({ success: result });
          emit(END);
        })
        .catch((err) => {
          emit({ err });
          emit(END);
        });
      return () => {};
    } else {
      const onProgress = ({ loaded }, file, chunkIndex) => {
        emit({ file, loaded, chunkIndex });
      };
      let course;
      actionFunc
        .then(async ({ data }) => {
          course = data;

          if (!!tags && !!tags.length && (!files || !files.length)) {
            await CourseService.updateVideoTags(
              course.id,
              tags
            ).catch((error) => {
              console.log("error", error);
            });
          }


          const studentImages = combineStudentImages(student0Image, student1Image, student2Image);

          emit({
            files: getFilesArray(files, videoThumbnail, courseThumbnailImage, studentImages) || [],
            filesTotalSize: getFilesTotalSize(files, videoThumbnail, courseThumbnailImage, studentImages) || 0,
          });
          const filesResponse =
            !!files &&
            !!files.length &&
            (await FilesService.uploadVideoFile({ files }, onProgress).catch(
              (error) => {
                throwCustomError(error);
              }
            ));
          const videoThumbnaiResponse = videoThumbnail && videoThumbnail.length && (await FilesService.uploadImageFile(
            { files: videoThumbnail },
            onProgress
          ).catch((error) => {
            throwCustomError(error);
          }));

          const courseThumbnailImageResponse = courseThumbnailImage && courseThumbnailImage.length &&(await FilesService.uploadImageFile(
            { files: courseThumbnailImage },
            onProgress
          ).catch((error) => {
            throwCustomError(error);
          }));

          const studentImagesResponse = studentImages && studentImages.length && (await FilesService.uploadImageFile(
            { files: studentImages },
            onProgress
          ).catch((error) => {
            throwCustomError(error);
          }));

          return { filesResponse, videoThumbnaiResponse, courseThumbnailImageResponse, studentImagesResponse };
        })
        .then(async ({ filesResponse, videoThumbnaiResponse, courseThumbnailImageResponse, studentImagesResponse }) => {
          let addCourseThumbnailImageFunc;
          let addStudentImagesFunc;
          
          if (!!courseThumbnailImageResponse) {
            addCourseThumbnailImageFunc = CourseService.addCourseThumbnailImage(course.id, {
              fileId: courseThumbnailImageResponse[0].file.id,
            }).catch((error) => {
              throwCustomError(error);
            });
          }

          // Handle student images mapping
          if (!!studentImagesResponse && course.studentsSays && course.studentsSays.length > 0) {
            const studentImagesMapping = [];
            const maxIndex = course.studentsSays.length - 1;
            let imageIndex = 0;

            // Map student0Image
            if (!!student0Image && !!student0Image.length && imageIndex < studentImagesResponse.length) {
              const targetIndex = Math.min(0, maxIndex);
              studentImagesMapping.push({
                courseStudentSayId: course.studentsSays[targetIndex].id,
                imageId: studentImagesResponse[imageIndex].file.id
              });
              imageIndex++;
            }

            // Map student1Image
            if (!!student1Image && !!student1Image.length && imageIndex < studentImagesResponse.length) {
              const targetIndex = Math.min(1, maxIndex);
              studentImagesMapping.push({
                courseStudentSayId: course.studentsSays[targetIndex].id,
                imageId: studentImagesResponse[imageIndex].file.id
              });
              imageIndex++;
            }

            // Map student2Image
            if (!!student2Image && !!student2Image.length && imageIndex < studentImagesResponse.length) {
              const targetIndex = Math.min(2, maxIndex);
              studentImagesMapping.push({
                courseStudentSayId: course.studentsSays[targetIndex].id,
                imageId: studentImagesResponse[imageIndex].file.id
              });
              imageIndex++;
            }

            if (studentImagesMapping.length > 0) {
              addStudentImagesFunc = CourseService.addCourseStudentSaysImages(course.id, studentImagesMapping).catch((error) => {
                throwCustomError(error);
              });
            }
          }

          
          if (!!filesResponse) {
            if (!!courseThumbnailImageResponse) {
              await addCourseThumbnailImageFunc;
            }
            if (!!addStudentImagesFunc) {
              await addStudentImagesFunc;
            }
            
            return CourseService.addVideo(course.id, {
              fileId: filesResponse[0].file.id,
              videoThumbnailId: videoThumbnaiResponse[0].file.id,
              tags: tags || []
            }).catch((error) => {
              throwCustomError(error);
            });
          } else if (!!videoThumbnaiResponse) {
            if (!!courseThumbnailImageResponse) {
              await addCourseThumbnailImageFunc;
            }
            if (!!addStudentImagesFunc) {
              await addStudentImagesFunc;
            }

            return CourseService.changeVideoThumbnail(
              course.id,
              videoThumbnaiResponse[0].file.id
            ).catch((error) => {
              throwCustomError(error);
            });
          } else if (!!courseThumbnailImageResponse) {
            if (!!addStudentImagesFunc) {
              await addStudentImagesFunc;
            }
            return addCourseThumbnailImageFunc;
          } else if (!!addStudentImagesFunc) {
            return addStudentImagesFunc;
          }
        })
        .then((result) => {
          emit({ success: result });
          emit(END);
        })
        .catch((err) => {
          emit({ err });
          emit(END);
        });
      return () => {};
    }
  });
};

const removeCourseAsync = async (id) => {
  return await CourseService.remove(id);
};

const changeCourseStatusAsync = async (id) => {
  return await CourseService.changeStatus(id);
};

const setCourseRecommendationsAsync = async (id, data) => {
  return await CourseService.setRecommendations(id, data);
}

const getVideoLibraryForCourseOptionAsync = async (searchParams) => {
  return await VideoLibraryService.getList(searchParams);
};

const getTutorsForCourseOptionAsync = async (searchParams) => {
  return await TutorService.getList(searchParams);
};

function* getCourseList({ payload }) {
  try {
    const response = yield call(getCourseListAsync, payload);
    yield put(getListCoursesSuccess(response));
  } catch (error) {
    yield put(getListCoursesError(error));
  }
}

function* getPaginatedCourseList({ payload }) {
  try {
    const response = yield call(getPaginatedCourseListAsync, payload);
    yield put(getPaginatedCourseListSuccess(response));
  } catch (error) {
    yield put(getPaginatedCourseListError(error));
  }
}

function* getCourse({ payload: { courseId } }) {
  try {
    const response = yield call(getCourseAsync, courseId);
    yield put(getCourseSuccess(response));
  } catch (error) {
    yield put(getCourseError(error));
  }
}

function* changeCourseStatus({ payload: { id } }) {
  try {
    const response = yield call(changeCourseStatusAsync, id);
    yield put(changeCourseStatusSuccess(response));
  } catch (error) {
    yield put(changeCourseStatusError(error));
  }
}

function* createCourseProgressListener(chan, categoryId, history) {
  while (true) {
    const { files, filesTotalSize, file, loaded, chunkIndex, err, success } =
      yield take(chan);
    if (!!files && !!filesTotalSize) {
      yield put(uploadInitiate({ files, filesTotalSize }));
    }
    if (err) {
      if (err.code === errorCodes.BAD_REQUEST_ERROR) {
        yield put(createCourseError(err));
      } else {
        yield put(uploadFailure(err));
        yield put(createCourseError(err));
        history.push(`/training/${categoryId}`);
      }
      return;
    }
    if (success) {
      yield put(uploadSuccess(success));
      yield put(createCourseSuccess(success));
      history.push(`/training/${categoryId}`);
      return;
    }
    if (!!file && !!loaded) {
      yield put(uploadProgress({ file, loaded, chunkIndex }));
    }
  }
}

function* createCourse({ payload: { data, history } }) {
  try {
    const { categoryId } = data;
    yield fork(
      createCourseProgressListener,
      courseFilesChannel(data),
      categoryId,
      history
    );
  } catch (error) {
    yield put(createCourseError(error));
  }
}

function* updateCourseProgressListener(chan, history, courseCategoryId) {
  while (true) {
    const { files, filesTotalSize, file, loaded, chunkIndex, err, success } =
      yield take(chan);
    if (!!files && !!filesTotalSize) {
      yield put(uploadInitiate({ files, filesTotalSize }));
    }
    if (err) {
      if (err.code === errorCodes.BAD_REQUEST_ERROR) {
        yield put(updateCourseError(err));
      } else {
        yield put(uploadFailure(err));
        yield put(updateCourseError(err));
        history.push(`/training/${courseCategoryId}`);
      }
      return;
    }
    if (success) {
      yield put(uploadSuccess(success));
      yield put(updateCourseSuccess(success));
      history.push(`/training/${courseCategoryId}`);
      return;
    }
    if (!!file && !!loaded) {
      yield put(uploadProgress({ file, loaded, chunkIndex }));
    }
  }
}

function* updateCourse({ payload: { data, history, courseCategoryId } }) {
  try {
    yield fork(
      updateCourseProgressListener,
      courseFilesChannel(data, "update"),
      history,
      courseCategoryId
    );
  } catch (error) {
    yield put(updateCourseError(error));
  }
}

function* removeCourse({ payload: { data } }) {
  try {
    const response = yield call(removeCourseAsync, data);
    yield put(removeCourseSuccess(response));
  } catch (error) {
    yield put(removeCourseError(error));
  }
}

function* updateCurseRecommendations({ payload: { data, courseId } }) {
  try {
    const response = yield call(setCourseRecommendationsAsync, courseId, data);
    yield put(setCourseRecommendationsSuccess(response));
  } catch (error) {
    yield put(setCourseRecommendationsError(error));
  }
}

function* getCoursesForRecommendations({payload: { limit = 0, offset = 0 }}) {
  try {
    const response = yield call(getCoursesForRecommendationsListAsync);
    yield put(getCoursesForRecommendationsSuccess(response));
  } catch (error) {
    yield put(getCoursesForRecommendationsError(error))
  }
}

function* getVideoLibraryForCourseOption({ payload }) {
  try {
    const response = yield call(getVideoLibraryForCourseOptionAsync, payload);
    yield put(getVideoLibraryForCourseOptionSuccess(response));
  } catch (error) {
    yield put(getVideoLibraryForCourseOptionError(error));
  }
}

function* getTutorsForCourseOption({ payload }) {
  try {
    const response = yield call(getTutorsForCourseOptionAsync, payload);
    yield put(getTutorsForCourseOptionSuccess(response));
  } catch (error) {
    yield put(getTutorsForCourseOptionError(error));
  }
}

export function* watchGetCourseList() {
  yield takeEvery(GET_LIST_COURSE, getCourseList);
}

export function* watchGetPaginatedCourseList() {
  yield takeEvery(GET_PAGINATED_COURSE_LIST, getPaginatedCourseList);
}

export function* watchCreateCourse() {
  yield takeEvery(CREATE_COURSE, createCourse);
}

export function* watchUpdateCourse() {
  yield takeEvery(UPDATE_COURSE, updateCourse);
}

export function* watchRemoveCourse() {
  yield takeEvery(REMOVE_COURSE, removeCourse);
}

export function* watchGetCourse() {
  yield takeEvery(GET_COURSE, getCourse);
}

export function* watchChangeCourseStatus() {
  yield takeEvery(CHANGE_COURSE_STATUS, changeCourseStatus);
}

export function* watchUpdateCurseRecommendations() {
  yield takeEvery(SET_COURSE_RECOMMENDATIONS, updateCurseRecommendations);
}

export function* watchGetCoursesForRecommendations() {
  yield takeEvery(GET_COURSES_FOR_RECOMMENDATIONS, getCoursesForRecommendations)
}

export function* watchGetVideoLibraryForCourseOption() {
  yield takeEvery(GET_VIDEO_LIBRARY_FOR_COURSE_OPTION, getVideoLibraryForCourseOption);
}

export function* watchGetTutorsForCourseOption() {
  yield takeEvery(GET_TUTORS_FOR_COURSE_OPTION, getTutorsForCourseOption);
}

function* coursesSaga() {
  yield all([
    fork(watchGetCourseList),
    fork(watchGetPaginatedCourseList),
    fork(watchCreateCourse),
    fork(watchUpdateCourse),
    fork(watchRemoveCourse),
    fork(watchGetCourse),
    fork(watchChangeCourseStatus),
    fork(watchUpdateCurseRecommendations),
    fork(watchGetCoursesForRecommendations),
    fork(watchGetVideoLibraryForCourseOption),
    fork(watchGetTutorsForCourseOption),
  ]);
}

export default coursesSaga;
